import Price from './Price'
import { Location } from './Spot'
import { Marker2 } from './Marker'
import { Provider } from './Provider'

export type ProductDestination = {
	name: string
	cover: string
	description: string
}

export type ProductItinerary = {
	day: number
	destinations: ProductDestination[]
	products?: Product[]
}

type Product =
	| TripProduct
	| AccommodationProduct
	| ActivityProduct
	| TransportProduct
	| IndispensableProduct

type BaseProduct = {
	id: string
	title: string
	description?: string
	category: PRODUCT_CATEGORY
	subcategory?: PRODUCT_SUB_CATEGORY
	price?: Price
	url?: string
	coverUrls: string[]
	isFavorite?: boolean
	languages?: ProductFilterLanguage[]
	provider?: Provider
}

type TripProduct = BaseProduct & {
	category: PRODUCT_CATEGORY.TRIP
	data?: Record<PRODUCT_DATA, number>
	itinerary?: ProductItinerary[]
	markers?: Marker2[]
}

type AccommodationProduct = BaseProduct & {
	category: PRODUCT_CATEGORY.ACCOMMODATION
	subcategory?: ACCOMMODATION_SUBCATEGORY
	data?: AccommodationData
}

type ActivityProduct = BaseProduct & {
	category: PRODUCT_CATEGORY.ACTIVITY
	data?: ActivityData
}

type TransportProduct = RentalTransportProduct | PublicTransportProduct

type RentalTransportProduct = BaseProduct & {
	category: PRODUCT_CATEGORY.TRANSPORT
	subcategory: TRANSPORT_SUBCATEGORY.RENTAL
	data?: TransportRentalData
}

type PublicTransportProduct = BaseProduct & {
	category: PRODUCT_CATEGORY.TRANSPORT
	subcategory: Exclude<TRANSPORT_SUBCATEGORY, TRANSPORT_SUBCATEGORY.RENTAL>
	data?: TransportData
}

type Activity = Product & {
	duration?: number
	location?: Location
	reviews?: number
	score?: number
}

enum PRODUCT_CATEGORY {
	TRIP = 'trip',
	ACCOMMODATION = 'accommodation',
	ACTIVITY = 'activity',
	TRANSPORT = 'transport',
	INDISPENSABLE = 'INDISPENSABLE',
}

export type PRODUCT_SUB_CATEGORY =
	| ACCOMMODATION_SUBCATEGORY
	| TRANSPORT_SUBCATEGORY
	| INDISPENSABLE_SUBCATEGORY

enum ACCOMMODATION_SUBCATEGORY {
	HOTEL = 'hotel',
	APARTMENT = 'apartment',
}

enum TRANSPORT_SUBCATEGORY {
	RENTAL = 'rental',
	TAXI = 'taxi',
	FLIGHT = 'flight',
	BOAT = 'boat',
	TRAIN = 'train',
}

enum INDISPENSABLE_SUBCATEGORY {
	BANK = 'bank',
	SIM = 'sim',
	INSURANCE = 'insurance',
	VISA = 'visa',
	RENTAL = 'rental',
}

type AccommodationData = {
	stars?: number
	nights?: number
	address?: string
}

type ActivityData = {
	startDate?: Date
	duration?: number
	score?: number
	reviews?: number
	availableLanguages?: string[]
	freeCancellation?: string
	accessibilityWheelchair?: boolean
	included?: string[]
	notIncluded?: string[]
	meetingPoint?: Location
	advanceMinutes?: number
}

type TransportData = {
	company?: string
	duration?: string
	baggageInfo?: string
	departure?: Date
	arrival?: Date
	originDestinationCode?: string
	targetDestinationCode?: string
	originAirportName?: string
	targetAirportName?: string
	dayDifference?: number
	numSegments?: number
}

type TransportRentalData = {
	company?: string
	pickupLocation?: string
	dropoffLocation?: string
}

type IndispensableProductData = {
	providerImage: string
	hasTermsAndConditions?: boolean
}

type IndispensableProduct = BaseProduct & {
	category: PRODUCT_CATEGORY.INDISPENSABLE
	subcategory: INDISPENSABLE_SUBCATEGORY
	data?: IndispensableProductData
}

enum PRODUCT_DATA {
	ADULTS = 'adults',
	CHILDREN = 'children',
	DESTINATIONS = 'destinations',
	ACCOMMODATIONS = 'accommodations',
	NIGHTS = 'nights',
	TRANSFERS = 'transfers',
	TOURS = 'tours',
	TRANSPORTS = 'transports',
}

enum PRODUCT_ORDER {
	FEATURED = 'featured',
	PRICE_ASC = 'price_asc',
	PRICES_DESC = 'price_desc',
	DURATION_ASC = 'duration_asc',
	DURATION_DESC = 'duration_desc',
}

type ProductFiltersNights = '1-5' | '6-10' | '11-15' | '16-90'
type ProductFilterLanguage = 'en' | 'es' | 'fr' | 'it' | 'pt'

type ProductFilters = {
	language?: ProductFilterLanguage
	range?: { min: number; max: number }
	favorites?: boolean
	accessibilityWheelchair?: boolean
	freeCancellation?: boolean
	selectedNights?: string[]
	nights?: ProductFiltersNights[]
	category: PRODUCT_CATEGORY
}

export default Product

export {
	Activity,
	PRODUCT_CATEGORY,
	ACCOMMODATION_SUBCATEGORY,
	TRANSPORT_SUBCATEGORY,
	AccommodationData,
	PRODUCT_ORDER,
	BaseProduct,
	TripProduct,
	AccommodationProduct,
	ActivityProduct,
	TransportProduct,
	RentalTransportProduct,
	PublicTransportProduct,
	ProductFilters,
	ProductFiltersNights,
	ProductFilterLanguage,
	IndispensableProduct,
	INDISPENSABLE_SUBCATEGORY,
}
